import { Injectable, NgZone } from '@angular/core';
import { environment } from '@env';
import * as firebase from 'firebase/app';
import 'firebase/firestore';
import { HCConfig, HCConfigFeatures, HCConfigRules } from '../../../hc-config/hc-config';
import {
  IDoctorSpecializationEntry,
  parseDoctorSpecializationEntry,
} from '../view-models/consts/doctor-specialization-registry';

type RemoteEnumConfig = Record<string, boolean>;

// O HC config vive num projeto Firebase separado (appmanager-35e9e), que tem Storage habilitado
// pros logos. Usamos um app Firebase secundario dedicado pra nao afetar o projeto principal
// (ptmp-d43bd), que continua servindo FCM/push, triggers realtime e polling.
const HC_CONFIG_FIREBASE_APP_NAME = 'hc-config';
const HC_CONFIG_FIREBASE_CONFIG = {
  apiKey: 'AIzaSyA5RenYVNINHJMHdz0Vo_6zYNF3MI8NjZA',
  authDomain: 'appmanager-35e9e.firebaseapp.com',
  projectId: 'appmanager-35e9e',
  storageBucket: 'appmanager-35e9e.firebasestorage.app',
  messagingSenderId: '551428530702',
  appId: '1:551428530702:web:d799d5b30f41d1f2d4925f',
};

interface IRemoteHCConfig extends Omit<HCConfig, 'features' | 'rules'> {
  enabled?: boolean;
  version?: number;
  features?: RemoteEnumConfig;
  rules?: RemoteEnumConfig;
}

@Injectable({
  providedIn: 'root',
})
export class HcRemoteConfigService {
  private readonly collectionName = 'hc-config';
  private readonly healthCentersCollectionName = 'health-centers';
  private readonly specialtiesCollectionName = 'specialties';

  constructor(private ngZone: NgZone) {}

  // Keeps a realtime Firestore listener open for the app's lifetime so HC config changes
  // (features, rules, assets...) reach the app without requiring a page reload.
  // The returned promise resolves once (first snapshot or timeout) so callers using this
  // during APP_INITIALIZER don't block bootstrap waiting for the listener to stay open.
  //
  // The listener is opened outside the Angular zone: Firestore's realtime channel is a
  // long-lived long-poll connection that Zone.js would otherwise track as a permanently
  // pending task, so ApplicationRef.isStable would never emit true again after the first
  // load, which blocks anything waiting on it (e.g. ServiceWorkerProviderService, used by
  // the post-login guard). Callbacks re-enter the zone so change detection keeps working.
  public listenToHealthCenters(onUpdate: (hcs: HCConfig[]) => void): Promise<void> {
    return new Promise((resolve) => {
      let hasResolved = false;
      const resolveOnce = () => {
        if (!hasResolved) {
          hasResolved = true;
          clearTimeout(timeoutId);
          resolve();
        }
      };
      const timeoutId = setTimeout(() => {
        console.error('[HcRemoteConfigService] Remote HC config timeout');
        resolveOnce();
      }, 3000);

      this.ngZone.runOutsideAngular(() => {
        this.getHealthCentersQuery().onSnapshot(
          (snapshot) => {
            this.ngZone.run(() => {
              onUpdate(
                snapshot.docs
                  .map((doc) => this.mapRemoteDocumentToHCConfig(doc))
                  .filter((config): config is HCConfig => !!config),
              );
              resolveOnce();
            });
          },
          (error) => {
            this.ngZone.run(() => {
              console.error('[HcRemoteConfigService] Error loading remote HC configs:', error);
              resolveOnce();
            });
          },
        );
      });
    });
  }

  // Same realtime/timeout/outside-the-zone approach as listenToHealthCenters, for the doctor
  // specializations kept at hc-config/{env}/specialties. The list is global per environment (not per HC).
  // Invalid documents are skipped and an empty result is not reported, so the caller keeps its fallback.
  public listenToSpecialties(onUpdate: (specialties: IDoctorSpecializationEntry[]) => void): Promise<void> {
    return new Promise((resolve) => {
      let hasResolved = false;
      const resolveOnce = () => {
        if (!hasResolved) {
          hasResolved = true;
          clearTimeout(timeoutId);
          resolve();
        }
      };
      const timeoutId = setTimeout(() => {
        console.error('[HcRemoteConfigService] Remote specialties timeout');
        resolveOnce();
      }, 3000);

      this.ngZone.runOutsideAngular(() => {
        this.getSpecialtiesQuery().onSnapshot(
          (snapshot) => {
            this.ngZone.run(() => {
              const specialties = snapshot.docs
                .map((doc) => parseDoctorSpecializationEntry(doc.data()))
                .filter((entry): entry is IDoctorSpecializationEntry => !!entry);

              if (specialties.length) {
                onUpdate(specialties);
              }

              resolveOnce();
            });
          },
          (error) => {
            this.ngZone.run(() => {
              console.error('[HcRemoteConfigService] Error loading remote specialties:', error);
              resolveOnce();
            });
          },
        );
      });
    });
  }

  private getFirestore(): firebase.firestore.Firestore {
    const app =
      firebase.apps.find((instance) => instance.name === HC_CONFIG_FIREBASE_APP_NAME) ||
      firebase.initializeApp(HC_CONFIG_FIREBASE_CONFIG, HC_CONFIG_FIREBASE_APP_NAME);

    return firebase.firestore(app);
  }

  private getHealthCentersQuery(): firebase.firestore.CollectionReference {
    const db = this.getFirestore();
    return db
      .collection(this.collectionName)
      .doc(this.getEnvironmentDocumentId())
      .collection(this.healthCentersCollectionName);
  }

  private getSpecialtiesQuery(): firebase.firestore.CollectionReference {
    return this.getFirestore()
      .collection(this.collectionName)
      .doc(this.getEnvironmentDocumentId())
      .collection(this.specialtiesCollectionName);
  }

  private mapRemoteDocumentToHCConfig(snapshot: firebase.firestore.QueryDocumentSnapshot): HCConfig | null {
    const data = snapshot.data() as IRemoteHCConfig;

    if (!this.isRemoteConfigSafeToUse(data)) {
      return null;
    }

    return {
      appName: data.appName,
      hcId: data.hcId,
      isPublic: data.isPublic,
      uf: data.uf,
      config: data.config,
      assets: data.assets,
      links: data.links,
      dataDeletion: data.dataDeletion,
      firstAccess: data.firstAccess,
      mealPlan: data.mealPlan,
      routes: data.routes,
      features: this.mapEnabledFlagKeysToEnumValues(data.features, HCConfigFeatures),
      rules: this.mapEnabledFlagKeysToEnumValues(data.rules, HCConfigRules),
    };
  }

  private mapEnabledFlagKeysToEnumValues<T extends Record<string, string>>(
    values: RemoteEnumConfig,
    enumMap: T,
  ): T[keyof T][] {
    return Object.keys(enumMap).reduce<T[keyof T][]>((enabledValues, key) => {
      if (values[key] === true) {
        enabledValues.push(enumMap[key as keyof T]);
      }

      return enabledValues;
    }, []);
  }

  private isRemoteConfigSafeToUse(
    data: IRemoteHCConfig,
  ): data is IRemoteHCConfig & {
    features: RemoteEnumConfig;
    rules: RemoteEnumConfig;
  } {
    // Remote config overrides the local HC config. Keep this validation strict enough
    // to prevent a malformed Firestore document from disabling local features/rules.
    return (
      data.enabled === true &&
      data.appName &&
      this.hasFieldValue(data.hcId) &&
      typeof data.isPublic === 'boolean' &&
      data.uf &&
      data.config &&
      data.assets &&
      data.links &&
      data.dataDeletion &&
      data.firstAccess &&
      data.mealPlan &&
      data.routes &&
      this.isBooleanFlagMap(data.features) &&
      this.isBooleanFlagMap(data.rules)
    );
  }

  private hasFieldValue(value: unknown): boolean {
    return value !== undefined && value !== null;
  }

  private isBooleanFlagMap(value: unknown): value is RemoteEnumConfig {
    // The import script writes features/rules as maps like { APAC: true }.
    // Reject missing, empty, array, or non-boolean values so the app falls back to local config.
    return (
      !!value &&
      typeof value === 'object' &&
      !Array.isArray(value) &&
      Object.keys(value).length > 0 &&
      Object.values(value).every((flagValue) => typeof flagValue === 'boolean')
    );
  }

  private getEnvironmentDocumentId(): string {
    if (this.isLocalhost()) {
      return 'localhost';
    }

    return environment.currentEnvironment || (environment.production ? 'production' : 'development');
  }

  private isLocalhost(): boolean {
    return (
      typeof window !== 'undefined' &&
      ['localhost', '127.0.0.1'].some((host) => window.location.hostname.includes(host))
    );
  }
}
